import React, { Suspense, lazy } from 'react';
import { ArrowLeft, ChevronDown } from 'react-feather';
import Button from '@/components/styled/Button';
import { SettingsType } from './SettingsProvider';
import { useTranslation } from 'react-i18next';

// Lazy load the Settings component
const Settings = lazy(() => import('@/components/settings/Settings'));

interface CollapsibleSectionProps {
    id: string;
    icon: any;
    title: string;
    type: SettingsType;
    hasNotification?: boolean;
    isExpanded: boolean;
    onToggle: (id: string) => void;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
    id,
    icon: Icon,
    title,
    type,
    hasNotification = false,
    isExpanded,
    onToggle,
}) => {
    return (
        <div className="border-b border-saga-gray-200 dark:border-zinc-600 last:border-b-0">
            <button
                onClick={() => onToggle(id)}
                className="w-full py-4 px-4 flex items-center justify-between hover:bg-saga-gray-50 dark:hover:bg-zinc-600/50 transition-colors"
            >
                <div className="flex items-center space-x-3">
                    <div className="relative">
                        {hasNotification && (
                            <div className="absolute top-0 left-0 w-2 h-2 bg-saga-orange rounded-full"></div>
                        )}
                        {typeof Icon === 'function' ? <Icon /> : <Icon size={20} />}
                    </div>
                    <span className="font-medium text-left">{title}</span>
                </div>
                <div className="transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    <ChevronDown size={20} />
                </div>
            </button>
            <div className={`grid transition-all duration-200 ease-in-out ${
                isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}>
                <div className="overflow-hidden">
                    <div className="bg-white dark:bg-zinc-700 px-4 py-4">
                        {isExpanded && (
                            <Suspense fallback={
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-saga-green"></div>
                                </div>
                            }>
                                <Settings type={type} />
                            </Suspense>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

interface SettingsMenuMobileProps {
    title: string;
    sections: {
        workspace: Array<{
            id: string;
            icon: any;
            title: string;
            type: SettingsType;
            visible: boolean;
            hasNotification?: boolean;
        }>;
        account: Array<{
            id: string;
            icon: any;
            title: string;
            type: SettingsType;
            hasNotification?: boolean;
        }>;
    };
    onClose: () => void;
    expandedSections: Set<string>;
    onToggleSection: (sectionId: string) => void;
}

export const SettingsMenuMobile: React.FC<SettingsMenuMobileProps> = ({
    title,
    sections,
    onClose,
    expandedSections,
    onToggleSection,
}) => {
    const { t } = useTranslation();

    return (
        <div className="absolute flex flex-col z-100 w-screen h-screen bg-saga-bg-gray-light dark:bg-saga-gray-1000 overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white dark:bg-zinc-700 border-b border-saga-gray-200 dark:border-zinc-600">
                <div className="flex items-center justify-between pr-2 h-14">
                    <div className="flex items-center space-x-2">
                        <button onClick={onClose} className="rounded mx-2 my-1 p-1 hover:bg-saga-gray-200 dark:hover:bg-zinc-600">
                            <ArrowLeft size={24} />
                        </button>
                        <div className="font-medium text-lg">Settings</div>
                    </div>
                    <Button.XButton onClick={onClose} />
                </div>
            </div>
            <div className="flex-1">
                {/* Workspace Settings */}
                <div className="px-4 pt-4 pb-2">
                    <h2 className="text-sm font-semibold text-saga-text-gray dark:text-saga-gray-300 uppercase tracking-wider">
                        {title}
                    </h2>
                </div>
                <div className="border-t border-saga-gray-200 dark:border-zinc-600">
                    {sections.workspace.filter(s => s.visible).map((section) => (
                        <CollapsibleSection
                            key={section.id}
                            {...section}
                            isExpanded={expandedSections.has(section.id)}
                            onToggle={onToggleSection}
                        />
                    ))}
                </div>

                {/* Account Settings */}
                <div className="px-4 pt-6 pb-2">
                    <h2 className="text-sm font-semibold text-saga-text-gray dark:text-saga-gray-300 uppercase tracking-wider">
                        {t('settings.sidebar.account')}
                    </h2>
                </div>
                <div className="border-t border-saga-gray-200 dark:border-zinc-600">
                    {sections.account.map((section) => (
                        <CollapsibleSection
                            key={section.id}
                            {...section}
                            isExpanded={expandedSections.has(section.id)}
                            onToggle={onToggleSection}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};